import { z } from 'zod';
import {
  ErrorCode,
  PARTNER_MATERIAL_LOCALES,
  PARTNER_MATERIAL_MAX_FILE_BYTES,
  PartnerMaterialCategorySchema,
  PartnerMaterialKindSchema,
  PartnerMaterialVideoUrlSchema,
  partnerMaterialFormatOf,
  type CreatePartnerMaterialRequest,
  type PartnerMaterialAdmin,
  type PartnerMaterialFileFormat,
  type PartnerMaterialLocale,
  type UpdatePartnerMaterialRequest
} from '@bliks-io/contracts';
import {
  LocalizedFormTextSchema,
  OptionalLocalizedFormTextSchema,
  optionalText,
  requiredText,
  toLocalizedFormText
} from '@/pages/admin/partner-content/localizedText';

export const MaterialFormSchema = z
  .object({
    kind: PartnerMaterialKindSchema,
    category: PartnerMaterialCategorySchema,
    title: LocalizedFormTextSchema,
    description: OptionalLocalizedFormTextSchema,
    videoUrl: z.string()
  })
  .superRefine((values, ctx) => {
    if (
      values.kind === 'video' &&
      !PartnerMaterialVideoUrlSchema.safeParse(values.videoUrl).success
    ) {
      ctx.addIssue({ code: 'custom', path: ['videoUrl'], message: ErrorCode.FIELD_INVALID_URL });
    }
  });

export type MaterialForm = z.infer<typeof MaterialFormSchema>;

/** Full keys, not templates: the translation check cannot resolve a template. */
export const MATERIAL_KIND_KEYS: Record<MaterialForm['kind'], { label: string; hint: string }> = {
  file: {
    label: 'partnerContent.materials.kinds.file',
    hint: 'partnerContent.materials.kindHints.file'
  },
  video: {
    label: 'partnerContent.materials.kinds.video',
    hint: 'partnerContent.materials.kindHints.video'
  }
};

export function toMaterialForm(material?: PartnerMaterialAdmin | null): MaterialForm {
  return {
    kind: material?.kind ?? 'file',
    category: material?.category ?? 'salesDocuments',
    title: toLocalizedFormText(material?.title),
    description: toLocalizedFormText(material?.description),
    videoUrl: material?.videoId ? `https://www.youtube.com/watch?v=${material.videoId}` : ''
  };
}

export function toCreateRequest(values: MaterialForm): CreatePartnerMaterialRequest {
  const description = optionalText(values.description);
  const common = {
    category: values.category,
    title: requiredText(values.title),
    ...(description ? { description } : {})
  };
  return values.kind === 'video'
    ? { kind: 'video', ...common, videoUrl: values.videoUrl }
    : { kind: 'file', ...common };
}

/** Sends a cleared description as null so the server removes it. */
export function toUpdateRequest(values: MaterialForm): UpdatePartnerMaterialRequest {
  return {
    category: values.category,
    title: requiredText(values.title),
    description: optionalText(values.description),
    ...(values.kind === 'video' ? { videoUrl: values.videoUrl } : {})
  };
}

/** File changes held in the panel until the editor saves. */
export interface PendingFiles {
  uploads: Partial<Record<PartnerMaterialLocale, File>>;
  removals: PartnerMaterialLocale[];
}

export const NO_PENDING_FILES: PendingFiles = { uploads: {}, removals: [] };

export type FileRejection = 'format' | 'size';

export const FILE_REJECTION_KEYS: Record<FileRejection, string> = {
  format: 'partnerContent.materials.files.rejected.format',
  size: 'partnerContent.materials.files.rejected.size'
};

/** Why a dropped file cannot become partner material, or its format when it can. */
export function checkMaterialFile(
  file: File
): { format: PartnerMaterialFileFormat } | { rejected: FileRejection } {
  const format = partnerMaterialFormatOf(file.type);
  if (!format) return { rejected: 'format' };
  if (file.size > PARTNER_MATERIAL_MAX_FILE_BYTES) return { rejected: 'size' };
  return { format };
}

/** Languages that will have a file once the pending changes are saved. */
export function languagesAfterSave(
  material: PartnerMaterialAdmin | null,
  pending: PendingFiles
): PartnerMaterialLocale[] {
  return PARTNER_MATERIAL_LOCALES.filter(
    (locale) =>
      Boolean(pending.uploads[locale]) ||
      (Boolean(material?.files[locale]) && !pending.removals.includes(locale))
  );
}

/** A file material without any file would show partners an empty row. */
export function canPublish(
  kind: MaterialForm['kind'],
  material: PartnerMaterialAdmin | null,
  pending: PendingFiles
): boolean {
  return kind === 'video' || languagesAfterSave(material, pending).length > 0;
}
